import * as React from "react";
import PropTypes from "prop-types";
import { InputWrapper, TextInput } from "../../Common/Inputs";
import {
  EMAIL_REGEX,
  format_phone_number,
  PHONE_PATTERN,
  EMAIL_PATTERN,
  DESIGNATION,
  COUNTRIES,
  US_STATES,
  CANADIAN_PROVINCES,
  maybeClassName
} from "../../../utils/inputs";
import Loading from "../../Common/Loading";
import { postInvestigatorForm } from "../../../api";
import { withFocusVisible, FocusVisiblePropType } from '../../Contexts/FocusVisibleManager';
import Listbox from "../../Common/Listbox";

class InvestigatorFormBase extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      form_data: {
        Country: COUNTRIES[0].value,
      },
      pending: false,
      result: undefined,
      error: undefined,
      invalid_fields: [],
      test: undefined,
    };

    this.focusable = {
      first: React.createRef(),
    }
  }

  componentDidMount() {
    const { hadKeyboardEvent } = this.props.focusVisible;
    if (hadKeyboardEvent && this.focusable.first.current) this.focusable.first.current.focus();
  }

  /**
   * Returns handler for a form field's change event, that lifts field value to this component's state.
   * @param {string} name Name of the field.
   * @param {function} get_value Function to get the value from the event object.
   */
  onChange = (name, get_value = (e => e.target.value)) => event => {
    this.setState({
      form_data: {
        ...this.state.form_data,
        [name]: get_value(event),
        ...(name === "Country" ? { Region: undefined } : {})
      }
    });
  };

  onSelect = name => opt => this.onChange(name, opt => opt.value)(opt);

  validateForm = () => {
    const { fields } = this.props.strings.investigator_form;
    const { form_data } = this.state;
    const phone = format_phone_number(form_data.Phone);

    const invalid_fields = [];
    Object.entries(fields).forEach(([name, field]) => {
      let valid = true;
      if (field.required && !form_data[name]) {
        valid = false;
      } else if (form_data[name]) {
        if (name === "Email") {
          valid = valid && EMAIL_REGEX.test(form_data[name]);
        } else if (name === "Phone") {
          valid = valid && !!phone;
        } else {
          valid = valid && form_data[name].length > 1
        }
      }
      if (!valid) {
        invalid_fields.push(name);
      }
    });
    if (invalid_fields.length > 0) {
      this.setState({ invalid_fields });
      return false;
    }

    return {
      ...form_data,
      Phone: phone,
    };
  };

  onSubmit = e => {
    e.preventDefault();
    const data = this.validateForm();
    if (!data) {
      return false;
    }
    this.setState({ pending: true }, () =>
      postInvestigatorForm(data)
        .then(this.handleRequestResponse)
    )
  };

  handleRequestResponse = res => {
    const { message, invalid_fields = [] } = this.getResponseError(res);
    this.setState({
      pending: false,
      result: res,
      error: message,
      invalid_fields,
    });
  };

  getResponseError = (result = this.state.result) => {
    if (!result || result.status === 200) {
      return {};
    }
    const { error } = result;
    const { strings } = this.props;
    if (!error) {
      return {
        message: strings.form.errors.generic
      };
    }
    const { code, message } = error;
    switch (code) {
      case "invalid_fields":
        return {
          invalid_fields: message,
          message: strings.form.errors.invalid_fields,
        };
      case "invalid_request":
        return {
          message
        };
      default:
        return {
          message: strings.form.errors.generic
        }
    }
  };

  // set attributes for the <input> based on the field name and strings
  make_field = ([name, field], inputProps = {}) => {

    const { invalid_fields } = this.state;
    const invalid = invalid_fields.includes(name);
    /**
     * When an invalid field's value changes, remove it from `state.invalid_fields`.
     *
     * @param {function} func Event handler.
     */
    const with_invalid = func => invalid
      ? (...args) => {
        this.setState({
          invalid_fields: invalid_fields.filter(f => f !== name),
        });
        return func(...args);
      }
      : func;

    let Component = TextInput;
    const instructionsId = field.instructions ? `${name}_instructions` : undefined;
    const titleProps = {
      id: `eff_field_${name}-title`
    }
    let props = {
      name,
      placeholder: field.placeholder || undefined,
      required: field.required,
      value: this.state.form_data[name],
      onChange: with_invalid(this.onChange(name)),
      'aria-labelledby': titleProps.id + maybeClassName(instructionsId),
      'aria-required': field.required || undefined,
    };
    let title = field.title;
    const { Country } = this.state.form_data;

    switch (name) {
      case "Phone":
        props = {
          ...props,
          pattern: PHONE_PATTERN,
          validate: v => !v || !!format_phone_number(v),
          type: "tel"
        };
        break;
      case "Email":
        props = {
          ...props,
          pattern: EMAIL_PATTERN,
          validate: v => EMAIL_REGEX.test(v),
          type: "email"
        };
        break;
      case "ProfessionalDesignation":
        Component = Listbox;
        props = {
          ...props,
          disabled: this.state.pending,
          onChange: with_invalid(this.onSelect(name)),
          options: DESIGNATION,
        };
        break;
      case "Country":
        Component = Listbox;
        props = {
          ...props,
          disabled: this.state.pending,
          onChange: with_invalid(this.onSelect(name)),
          options: COUNTRIES,
        };
        break;
      case "Region":
        if (Country === "United States" || Country === "Canada") {
          Component = Listbox;
          props = {
            ...props,
            disabled: this.state.pending,
            onChange: with_invalid(this.onSelect(name)),
            options: Country === "United States" ? US_STATES : CANADIAN_PROVINCES,
          };
          title = field.title
        } else {
          title = field.title
        }
        break;
      case "ExpiryDate":
        props = {
          ...props,
          type: "date"
        };
        break;
    }

    return (
      <InputWrapper key={name} title={title} required={field.required} titleProps={titleProps} className="eff__field" invalid={invalid}>
        {instructionsId && <span className="sr-only" id={instructionsId}>{field.instructions}</span>}
        <Component {...inputProps} {...props} />
      </InputWrapper>
    )
  };

  render() {
    const { method, url, strings } = this.props;
    const { investigator_form, form } = strings;
    const { pending, result, error } = this.state;
    const success = !pending && result && result.status === 200;
    const fields = Object.entries(investigator_form.fields).map(this.make_field).filter(f => !!f);
    const firstFocusable = fields.findIndex(field => !field.props.disabled);
    if (-1 !== firstFocusable) fields[firstFocusable] = this.make_field(Object.entries(investigator_form.fields)[firstFocusable], { innerRef: this.focusable.first });

    return <div className="external-funding-form">
      <form method={method} action={url} className="eff__form" onSubmit={this.onSubmit}>
        <div className="wysiwyg-content centered eff__instructions">
          <p>{form.required_instructions}</p>
        </div>
        <fieldset disabled={pending || success}>
          <div className="eff__fields" data-collapse={success}>
            <div className="eff__column_4">
              {fields.slice(0, 4)}
            </div>
            <div className="eff__column">
              {fields.slice(4, 8)}
              {fields.slice(14)}
            </div>
            <div className="eff__column">
              {fields.slice(8, 14)}
            </div>
            <div className="eff__error-msg">
              {error}
            </div>
          </div>
        </fieldset>
        <div className="eff__submit-bar eff__field--full-width">
          <button data-pending={pending} data-success={!!success} className="eff__submit-btn btn btn-secondary" type="submit">
            <div data-part="text">{form.components.submit}</div>
            <div data-part="pending"><Loading /></div>
            <div data-part="success">{form.components.success}</div>
          </button>
        </div>
      </form>
    </div>
  }
}

InvestigatorFormBase.propTypes = {
  method: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  strings: PropTypes.object.isRequired,
  focusVisible: FocusVisiblePropType.isRequired,
};

export const InvestigatorForm = withFocusVisible(InvestigatorFormBase);

InvestigatorForm.propTypes = {
  method: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  strings: PropTypes.object.isRequired,
};
